<template>
  <v-card>
    <v-card-title>
      Meal Bin/Rack Assignment Configuration
      <v-spacer/>
      <v-alert v-if="!noChanges" type="warning" outlined dense class="mb-0 mr-2">
        Unsaved changes: {{ changeSummary }}
      </v-alert>
      <v-spacer/>
      <v-btn
        icon
        class="mr-2"
        title="Download settings as a file"
        @click="exportSettings()">
        <v-icon>mdi-download</v-icon>
      </v-btn>
      <v-btn
        icon
        class="mr-2"
        title="Import settings from a file"
        @click="$refs.importInput.click()">
        <v-icon>mdi-upload</v-icon>
      </v-btn>
      <input
        ref="importInput"
        type="file"
        accept=".json,application/json"
        class="d-none"
        @change="importSettings($event)"
      />
      <v-btn
        class="mr-2"
        color="secondary"
        @click="addRack()">
        Add Rack
      </v-btn>
      <v-btn
        :disabled="noChanges"
        color="primary"
        @click="save()">
        Save
      </v-btn>
    </v-card-title>
    <v-card-text class="pt-0">
      <v-simple-table dense>
        <thead>
        <tr>
          <th class="text-left">Rack</th>
          <th class="text-right">Boxes</th>
          <th class="text-right">Racks</th>
          <th class="text-right">Layout</th>
          <th class="text-right">Meals/Bin</th>
          <th class="text-left">Locations</th>
          <th class="text-left">Shipping</th>
          <th class="text-left">Pickup</th>
          <th/>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(rackConfig,index) of rackConfigs" v-bind:key="index"
          :class="{'changed-row': rackChanged(index)}">
          <td :class="{'font-weight-bold': rackFieldChanged(index, 'boxType')}">
            {{ rackConfig.boxType || 'Unnamed Rack' }}
          </td>
          <td class="text-right"
              :class="{'font-weight-bold': rackFieldChanged(index, 'maxRacks', 'rowsOfBins', 'binsPerRow')}">
            {{ getBoxCount(rackConfig) }}
          </td>
          <td class="text-right" :class="{'font-weight-bold': rackFieldChanged(index, 'maxRacks')}">
            {{ rackConfig.maxRacks }}
          </td>
          <td class="text-right" :class="{'font-weight-bold': rackFieldChanged(index, 'rowsOfBins', 'binsPerRow')}">
            {{ rackConfig.rowsOfBins }}×{{ rackConfig.binsPerRow }}
          </td>
          <td class="text-right"
              :class="{'font-weight-bold': rackFieldChanged(index, 'mealsPerBinMinimum', 'mealsPerBin')}">
            {{ rackConfig.mealsPerBinMinimum }}–{{ rackConfig.mealsPerBin }}
          </td>
          <td :class="{'font-weight-bold': rackFieldChanged(index, 'locations')}">
            <v-chip
              v-for="location of rackConfig.locations" v-bind:key="location"
              x-small class="mr-1">
              {{ location }}
            </v-chip>
          </td>
          <td :class="{'font-weight-bold': rackFieldChanged(index, 'shippingGroup', 'splitOnly', 'stickersPerBox')}">
            <v-chip v-if="rackConfig.shippingGroup" x-small color="secondary" outlined class="mr-1">
              {{ rackConfig.shippingGroup }} only
            </v-chip>
            <v-chip v-if="rackConfig.splitOnly" x-small color="warning" outlined class="mr-1">
              split only
            </v-chip>
            <v-chip v-if="rackConfig.stickersPerBox > 1" x-small color="info" outlined>
              {{ rackConfig.stickersPerBox }} stickers/box
            </v-chip>
          </td>
          <td :class="{'font-weight-bold': rackFieldChanged(index, 'includePickupOrders')}">
            <v-icon v-if="rackConfig.includePickupOrders" small color="primary">mdi-check</v-icon>
          </td>
          <td class="text-right">
            <v-btn icon small color="primary" @click="openEdit(index)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </td>
        </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>

    <v-card-title>
      Box Splitting Rules
      <v-spacer/>
      <v-btn color="secondary" @click="addRule()">
        Add Rule
      </v-btn>
    </v-card-title>
    <v-card-subtitle>
      When the source box type's racks run out during bin assignment, matching orders are
      split into one of each target box instead (before falling back to the old bin racks).
    </v-card-subtitle>
    <v-card-text class="pt-0">
      <v-simple-table dense>
        <thead>
        <tr>
          <th class="text-left">Rule</th>
          <th class="text-left">Locations</th>
          <th class="text-left">Shipping</th>
          <th class="text-right">Priority</th>
          <th/>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(rule,index) of splitRules" v-bind:key="'rule-'+index"
          :class="{'changed-row': ruleChanged(index)}">
          <td :class="{'font-weight-bold': ruleFieldChanged(index, 'sourceBoxType', 'targetBoxType1', 'targetBoxType2')}">
            {{ rule.sourceBoxType || '?' }} → {{ rule.targetBoxType1 || '?' }} + {{ rule.targetBoxType2 || '?' }}
          </td>
          <td :class="{'font-weight-bold': ruleFieldChanged(index, 'locations')}">
            <v-chip
              v-for="location of rule.locations" v-bind:key="location"
              x-small class="mr-1">
              {{ location }}
            </v-chip>
          </td>
          <td :class="{'font-weight-bold': ruleFieldChanged(index, 'shippingGroup')}">
            <v-chip v-if="rule.shippingGroup" x-small color="secondary" outlined>
              {{ rule.shippingGroup }} only
            </v-chip>
          </td>
          <td class="text-right" :class="{'font-weight-bold': ruleFieldChanged(index, 'priority')}">
            {{ rule.priority || 0 }}
          </td>
          <td class="text-right">
            <v-btn icon small color="primary" @click="openRuleEdit(index)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </td>
        </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>

    <v-dialog v-model="dialog" max-width="800" persistent>
      <v-card v-if="editingConfig">
        <v-card-title>Edit Rack</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingConfig.boxType"
                label="Rack Name"
                @change="parseChange(editingConfig)"
                :rules="rackNameRules()"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingConfig.maxRacks"
                label="How Many Racks" type="number"
                @change="parseChange(editingConfig)"/>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingConfig.mealsPerBinMinimum"
                :max="editingConfig.mealsPerBin" label="Minimum Meals Per Bin"
                step="0.5"
                type="number"
                @change="parseChange(editingConfig)"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingConfig.mealsPerBin"
                :min="editingConfig.mealsPerBinMinimum"
                label="Maximum Meals Per Bin"
                type="number"
                step="0.5"
                @change="parseChange(editingConfig)"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="editingConfig.rowsOfBins" label="Rows of Bins" type="number"
                            @change="parseChange(editingConfig)"/>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="editingConfig.binsPerRow" label="Bins per Row" type="number"
                            @change="parseChange(editingConfig)"/>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingConfig.stickersPerBox"
                label="Stickers per box"
                type="number"
                min="1"
                hint="Each box on this rack prints this many stickers, each with its own QR code"
                persistent-hint
                @change="parseChange(editingConfig)"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="editingConfig.locations" :items="['home','office']" chips label="Locations"
                multiple @change="parseChange(editingConfig)"/>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="editingConfig.shippingGroup"
                :items="['local','ship']"
                label="Restrict to shipping group"
                clearable
                hint="If set, only orders in this shipping group will be assigned bins in this rack type"
                persistent-hint
                @change="parseChange(editingConfig)"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="editingConfig.includePickupOrders"
                label="Include pickup orders"
                hint="If unchecked, orders addressed to a pickup location will not be assigned bins in this rack type"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="editingConfig.splitOnly"
                label="Reserved for split boxes"
                hint="If checked, only boxes created by a box splitting rule are assigned bins in this rack type"
                persistent-hint
              />
            </v-col>
          </v-row>
          <strong>{{ getBoxCount(editingConfig) }} boxes</strong>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="error" @click="deleteRack()">Delete</v-btn>
          <v-spacer/>
          <v-btn text @click="cancelEdit()">Cancel</v-btn>
          <v-btn color="primary" @click="doneEdit()">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="ruleDialog" max-width="500" persistent>
      <v-card v-if="editingRule">
        <v-card-title>Edit Box Splitting Rule</v-card-title>
        <v-card-text>
          <v-select
            v-model="editingRule.sourceBoxType"
            :items="boxTypeOptions"
            label="Source box (the one that ran out)"
          />
          <v-select
            v-model="editingRule.targetBoxType1"
            :items="boxTypeOptions"
            label="Target box 1 (bigger box, filled first)"
          />
          <v-select
            v-model="editingRule.targetBoxType2"
            :items="boxTypeOptions"
            label="Target box 2"
          />
          <v-select
            v-model="editingRule.locations" :items="['home','office']" chips label="Locations"
            multiple
            hint="If set, only orders for these locations are split"
            persistent-hint
          />
          <v-select
            v-model="editingRule.shippingGroup"
            :items="['local','ship']"
            label="Restrict to shipping group"
            clearable
            hint="If set, only orders in this shipping group are split"
            persistent-hint
          />
          <v-text-field
            v-model="editingRule.priority"
            label="Priority" type="number"
            hint="Rules with a lower priority are applied first"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-btn text color="error" @click="deleteEditingRule()">Delete</v-btn>
          <v-spacer/>
          <v-btn text @click="cancelRuleEdit()">Cancel</v-btn>
          <v-btn color="primary" :disabled="!editingRuleValid" @click="doneRuleEdit()">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped>
tr.changed-row td {
  background-color: rgba(255, 193, 7, 0.15);
}
</style>


<script>
import urlState from "@/router/urlState";
import api from "@/api";
import {deepCopy} from "@/store/utils";

export default {
  mixins: [urlState],
  data() {
    return {
      rackConfigs: [],
      rackConfigsOriginal: [],
      dialog: false,
      editingIndex: null,
      editingConfig: null,
      editingSnapshot: null,
      editingIsNew: false,
      deletedRackIds: [],
      splitRules: [],
      splitRulesOriginal: [],
      deletedRuleIds: [],
      ruleDialog: false,
      editingRuleIndex: null,
      editingRule: null,
      editingRuleSnapshot: null,
      editingRuleIsNew: false
    }
  },
  async mounted() {
    await this.loadConfigs();

  },
  computed: {
    noChanges() {
      if (this.deletedRackIds.length > 0 || this.deletedRuleIds.length > 0) {
        return false;
      }
      if (this.rackConfigs.some((config, index) => this.rackChanged(index))) {
        return false;
      }
      if (this.splitRules.some((rule, index) => this.ruleChanged(index))) {
        return false;
      }
      return true;
    },
    changeSummary() {
      const parts = [];
      const count = (n, noun, verb) => {
        if (n > 0) {
          parts.push(`${n} ${noun}${n === 1 ? '' : 's'} ${verb}`);
        }
      };
      count(this.rackConfigs.filter(config => !config.id).length, 'rack', 'added');
      count(this.rackConfigs.filter((config, index) => config.id && this.rackChanged(index)).length, 'rack', 'changed');
      count(this.deletedRackIds.length, 'rack', 'deleted');
      count(this.splitRules.filter(rule => !rule.id).length, 'rule', 'added');
      count(this.splitRules.filter((rule, index) => rule.id && this.ruleChanged(index)).length, 'rule', 'changed');
      count(this.deletedRuleIds.length, 'rule', 'deleted');
      return parts.join(', ');
    },
    boxTypeOptions() {
      return this.rackConfigs.map(c => c.boxType).filter(boxType => !!boxType);
    },
    editingRuleValid() {
      const rule = this.editingRule;
      if (!rule) {
        return false;
      }
      return !!(rule.sourceBoxType && rule.targetBoxType1 && rule.targetBoxType2)
        && rule.targetBoxType1 !== rule.sourceBoxType
        && rule.targetBoxType2 !== rule.sourceBoxType
        && rule.targetBoxType1 !== rule.targetBoxType2;
    }
  },
  methods: {
    rackNameRules() {
      return [
        v => !!v || 'Rack name is required',
        v => /^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)?$/.test(v) || `rack name must follow the rules of alphanumeric with at most a single space.  eg these are ok:  "Ash", "Office Ash", "O Ash"`
      ]
    },
    async loadConfigs() {
      this.rackConfigs = await api.get('v2/order/rack-configs').then(r => r.data);
      this.rackConfigs.sort((a, b) => a.mealsPerBinMinimum - b.mealsPerBinMinimum);
      this.rackConfigsOriginal = deepCopy(this.rackConfigs);
      this.splitRules = await api.get('v2/order/box-split-rules').then(r => r.data);
      this.splitRulesOriginal = deepCopy(this.splitRules);
      this.deletedRackIds = [];
      this.deletedRuleIds = [];
    },
    addRack() {
      // No id => saved as a new rack definition (backend creates on POST).
      this.rackConfigs.push({
        boxType: '',
        maxRacks: 1,
        mealsPerBinMinimum: 0,
        mealsPerBin: 0,
        rowsOfBins: 1,
        binsPerRow: 1,
        locations: [],
        includePickupOrders: false,
        shippingGroup: null,
        splitOnly: false,
        stickersPerBox: 1
      });
      this.openEdit(this.rackConfigs.length - 1, true);
    },
    openEdit(index, isNew = false) {
      this.editingIndex = index;
      this.editingConfig = this.rackConfigs[index];
      this.editingSnapshot = deepCopy(this.rackConfigs[index]);
      this.editingIsNew = isNew;
      this.dialog = true;
    },
    cancelEdit() {
      if (this.editingIsNew) {
        // Discard a rack that was only just added.
        this.rackConfigs.splice(this.editingIndex, 1);
      } else {
        // Restore the values as they were before editing.
        this.$set(this.rackConfigs, this.editingIndex, this.editingSnapshot);
      }
      this.closeDialog();
    },
    doneEdit() {
      this.parseChange(this.editingConfig);
      this.closeDialog();
    },
    deleteRack() {
      const config = this.editingConfig;
      if (config.id) {
        // Deleted on save; reload the page to undo.
        this.deletedRackIds.push(config.id);
      }
      this.rackConfigs.splice(this.editingIndex, 1);
      this.closeDialog();
    },
    closeDialog() {
      this.dialog = false;
      this.editingIndex = null;
      this.editingConfig = null;
      this.editingSnapshot = null;
      this.editingIsNew = false;
    },
    async save() {
      const savePromises = [];
      for (const config of this.rackConfigs) {
        if (JSON.stringify(this.originalRack(config)) === JSON.stringify(config)) {
          continue;
        }
        this.parseChange(config); // in case it isn't validated
        savePromises.push(api.post('v2/order/rack-configs', config));
      }
      for (const id of this.deletedRackIds) {
        savePromises.push(api.delete(`v2/order/rack-configs/${id}`));
      }
      for (const id of this.deletedRuleIds) {
        savePromises.push(api.delete(`v2/order/box-split-rules/${id}`));
      }
      for (const rule of this.splitRules) {
        if (JSON.stringify(this.originalRule(rule)) === JSON.stringify(rule)) {
          continue;
        }
        this.parseRuleChange(rule);
        savePromises.push(api.post('v2/order/box-split-rules', rule));
      }
      try {
        await Promise.all(savePromises);
      } catch (e) {
        const message = (e.response && e.response.data && e.response.data.error) || e.message;
        alert(`Some changes could not be saved (${message}). `
          + 'Reloading the saved settings — check what is missing and re-apply it (or import your file again).');
      }
      await this.loadConfigs();
    },
    addRule() {
      // No id => saved as a new rule (backend creates on POST).
      this.splitRules.push({
        sourceBoxType: null,
        targetBoxType1: null,
        targetBoxType2: null,
        locations: [],
        shippingGroup: null,
        priority: 0
      });
      this.openRuleEdit(this.splitRules.length - 1, true);
    },
    openRuleEdit(index, isNew = false) {
      this.editingRuleIndex = index;
      this.editingRule = this.splitRules[index];
      this.editingRuleSnapshot = deepCopy(this.splitRules[index]);
      this.editingRuleIsNew = isNew;
      this.ruleDialog = true;
    },
    cancelRuleEdit() {
      if (this.editingRuleIsNew) {
        // Discard a rule that was only just added.
        this.splitRules.splice(this.editingRuleIndex, 1);
      } else {
        // Restore the values as they were before editing.
        this.$set(this.splitRules, this.editingRuleIndex, this.editingRuleSnapshot);
      }
      this.closeRuleDialog();
    },
    doneRuleEdit() {
      this.parseRuleChange(this.editingRule);
      this.closeRuleDialog();
    },
    closeRuleDialog() {
      this.ruleDialog = false;
      this.editingRuleIndex = null;
      this.editingRule = null;
      this.editingRuleSnapshot = null;
      this.editingRuleIsNew = false;
    },
    deleteRule(index) {
      const rule = this.splitRules[index];
      if (rule.id) {
        this.deletedRuleIds.push(rule.id);
      }
      this.splitRules.splice(index, 1);
    },
    deleteEditingRule() {
      this.deleteRule(this.editingRuleIndex);
      this.closeRuleDialog();
    },
    parseRuleChange(rule) {
      rule.priority = Number(rule.priority) || 0;
      rule.shippingGroup = rule.shippingGroup || null;
      rule.locations = rule.locations || [];
    },
    parseChange(config) {
      config.boxType = (config.boxType||'').trim();
      config.maxRacks = Number(config.maxRacks);
      config.mealsPerBinMinimum = Number(config.mealsPerBinMinimum);
      config.mealsPerBin = Number(config.mealsPerBin);
      config.rowsOfBins = Number(config.rowsOfBins);
      config.binsPerRow = Number(config.binsPerRow);
      if (config.locations && config.locations.split) {
        config.locations = config.locations.split(',').map(s => s.trim()).filter(s => !!s);
      }
      config.includePickupOrders = !!config.includePickupOrders;
      config.shippingGroup = config.shippingGroup || null;
      // also fills in configs from settings files that predate the field
      config.stickersPerBox = Math.max(1, Number(config.stickersPerBox) || 1);
    },
    getBoxCount(config) {
      return config.maxRacks * config.rowsOfBins * config.binsPerRow;
    },
    originalRack(config) {
      return config.id ? this.rackConfigsOriginal.find(original => original.id === config.id) : undefined;
    },
    rackChanged(index) {
      const config = this.rackConfigs[index];
      return JSON.stringify(this.originalRack(config)) !== JSON.stringify(config);
    },
    rackFieldChanged(index, ...fields) {
      const config = this.rackConfigs[index];
      return this.fieldChanged(this.originalRack(config), config, fields);
    },
    originalRule(rule) {
      return rule.id ? this.splitRulesOriginal.find(original => original.id === rule.id) : undefined;
    },
    ruleChanged(index) {
      const rule = this.splitRules[index];
      return JSON.stringify(this.originalRule(rule)) !== JSON.stringify(rule);
    },
    ruleFieldChanged(index, ...fields) {
      const rule = this.splitRules[index];
      return this.fieldChanged(this.originalRule(rule), rule, fields);
    },
    fieldChanged(original, current, fields) {
      if (!original) {
        // A row added since the last save is entirely new.
        return true;
      }
      return fields.some(field => JSON.stringify(original[field]) !== JSON.stringify(current[field]));
    },
    exportSettings() {
      const settings = {
        rackConfigs: this.rackConfigs,
        splitRules: this.splitRules
      };
      const blob = new Blob([JSON.stringify(settings, null, 2)], {type: 'application/json'});
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `rack-assignment-settings-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    importSettings(event) {
      const file = event.target.files && event.target.files[0];
      // Allow re-importing the same file later.
      event.target.value = '';
      if (!file) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        let settings;
        try {
          settings = JSON.parse(reader.result);
        } catch (e) {
          alert('Could not read settings: the file is not valid JSON.');
          return;
        }
        if (!Array.isArray(settings.rackConfigs) || !Array.isArray(settings.splitRules)) {
          alert('Could not read settings: expected a file with rackConfigs and splitRules lists.');
          return;
        }
        settings.rackConfigs.forEach(config => this.parseChange(config));
        settings.splitRules.forEach(rule => this.parseRuleChange(rule));
        this.rackConfigs = settings.rackConfigs;
        this.splitRules = settings.splitRules;
        // Existing racks and rules missing from the imported file get deleted on save.
        const importedRackIds = new Set(this.rackConfigs.map(config => config.id).filter(id => !!id));
        this.deletedRackIds = this.rackConfigsOriginal
          .map(config => config.id)
          .filter(id => !!id && !importedRackIds.has(id));
        const importedRuleIds = new Set(this.splitRules.map(rule => rule.id).filter(id => !!id));
        this.deletedRuleIds = this.splitRulesOriginal
          .map(rule => rule.id)
          .filter(id => !!id && !importedRuleIds.has(id));
      };
      reader.readAsText(file);
    }
  },
  setup() {
  }
}
</script>
